/**
 * UNICO formatador de apresentacao (pt-BR). Entrada: string decimal vinda da API ("1234.5", "-0.10"). Nenhuma tela,
 * grade, chip, PDF-view ou exportacao formata dinheiro, percentual, markup ou custo por conta propria (o teste
 * `format-unico.test.ts` falha se surgir `Intl.NumberFormat`, `toFixed`, `toLocaleString` ou "R$" montado fora daqui).
 *
 * Regras:
 * - Nada aqui calcula preco, custo, margem ou imposto. Arredondar para MOSTRAR e a unica "conta", feita sobre a string
 *   (BigInt, sem Number), HALF_UP (o ROUND do Excel: empate vai para longe do zero) nas casas da empresa
 *   (Empresa > Parametros > Exibicao). O valor que sobe para a API nunca passa por aqui.
 * - Dinheiro (R$ preco/total/lucro), percentual, markup e custo (custo unitario e CPM) tem casas proprias, de 0 a 10.
 * - A parte inteira passa por Intl com BigInt (sem Number, sem perda de precisao).
 * - Valor invalido vira '—' (nunca NaN na tela).
 */
import { arredondarHalfUp, CASAS_PADRAO, casasDoTipo, type ExibicaoEmpresaDto, type TipoCasas } from '@triade/contracts';

const ptBR = new Intl.NumberFormat('pt-BR');

// ---- Casas da empresa (estado do modulo, definido pelo provedor do shell; padrao = contrato) ----
let casasAtuais: ExibicaoEmpresaDto = { ...CASAS_PADRAO };

/** Define as casas de exibicao em vigor (chamado pelo provedor depois de ler GET /empresa/exibicao). */
export function definirCasasExibicao(c: ExibicaoEmpresaDto): void {
  casasAtuais = { casasDinheiro: c.casasDinheiro, casasPercentual: c.casasPercentual, casasMarkup: c.casasMarkup, casasCusto: c.casasCusto };
}
export function casasExibicao(): ExibicaoEmpresaDto {
  return casasAtuais;
}
/** Chave estavel das casas em vigor (para remontar telas quando mudam). */
export function assinaturaCasas(c: ExibicaoEmpresaDto = casasAtuais): string {
  return `${c.casasDinheiro}.${c.casasPercentual}.${c.casasMarkup}.${c.casasCusto}`;
}
const SEPARADOR_DECIMAL = ptBR.formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? ',';
const RE_DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;
const HORA = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });

/** true se a string e um decimal aceito (sem notacao cientifica, com ponto como separador). */
export function ehDecimal(valor: string | null | undefined): valor is string {
  return typeof valor === 'string' && RE_DECIMAL.test(valor.trim());
}

/** Formata string decimal para pt-BR. `minCasas` completa com zeros a direita; nunca arredonda. */
export function formatarDecimal(valor: string | null | undefined, minCasas = 2): string {
  if (!ehDecimal(valor)) return '—';
  const m = RE_DECIMAL.exec(valor.trim());
  if (!m) return '—';
  const sinal = m[1] ?? '';
  const inteiro = m[2] ?? '0';
  const frac = m[3] ?? '';
  const grupos = ptBR.format(BigInt(inteiro));
  const casas = frac.padEnd(minCasas, '0');
  return `${sinal}${grupos}${casas ? `${SEPARADOR_DECIMAL}${casas}` : ''}`;
}

/**
 * Arredonda (HALF_UP) para `casas` e formata em pt-BR com exatamente essas casas. Base de todos os formatadores
 * configuraveis. Invalido: '—'.
 */
export function formatarArredondado(valor: string | null | undefined, casas: number): string {
  if (!ehDecimal(valor)) return '—';
  const r = arredondarHalfUp(valor.trim(), casas);
  return r === null ? '—' : formatarDecimal(r, casas);
}

export const comMoeda = (texto: string): string => (texto === '—' ? texto : texto.startsWith('-') ? `-R$ ${texto.slice(1)}` : `R$ ${texto}`);

/**
 * Valor COMPLETO (precisao do banco, sem arredondar) em R$, so sem os zeros de escala alem de 2 casas ("0.275350" vira
 * "R$ 0,27535"). Para dicas e detalhes em que o usuario quer ver tudo o que esta gravado.
 */
export function formatarMoedaCompleta(valor: string | null | undefined): string {
  const t = formatarNumeroCompleto(valor);
  return t === '—' ? t : comMoeda(t);
}

/** Numero COMPLETO (sem arredondar), sem os zeros de escala alem de 2 casas: "21.279000" vira "21,279"; "0.8" vira "0,80". */
export function formatarNumeroCompleto(valor: string | null | undefined): string {
  if (!ehDecimal(valor)) return '—';
  const v = valor.trim();
  return formatarDecimal(v.includes('.') ? v.replace(/(\.\d{2}\d*?)0+$/, '$1') : v, 2);
}

/**
 * Texto de campo (o que o usuario digitou, pt-BR: "25,5", "1.234,567891") mostrado nas casas do tipo, HALF_UP. Nunca altera o
 * texto guardado: e so para exibir quando o campo nao esta em edicao. Texto vazio ou invalido volta como veio.
 */
export function formatarTextoDigitado(texto: string | null | undefined, tipo: TipoCasas): string {
  const bruto = texto ?? '';
  const dec = decimalDigitado(bruto);
  return dec === null ? bruto : formatarValor(dec, tipo);
}

/** Valor no tipo de casas pedido, sem simbolo de moeda ("1.234,50"). Percentual aqui e em pontos percentuais. */
export function formatarValor(valor: string | null | undefined, tipo: TipoCasas): string {
  return formatarArredondado(valor, casasDoTipo(casasAtuais, tipo));
}

/** Dinheiro (preco, total, lucro em R$) sem simbolo: "1.234,50" (casas de dinheiro da empresa). */
export function formatarDinheiro(valor: string | null | undefined): string {
  return formatarValor(valor, 'dinheiro');
}

/** Moeda BRL nas casas de dinheiro da empresa: "R$ 23,45". Negativo: "-R$ 1,00". */
export function formatarMoeda(valor: string | null | undefined): string {
  return comMoeda(formatarDinheiro(valor));
}

/** Custo unitario e CPM sem simbolo, nas casas de custo da empresa. */
export function formatarCustoNumero(valor: string | null | undefined): string {
  return formatarValor(valor, 'custo');
}

/** Custo unitario e CPM em R$, nas casas de custo da empresa: "R$ 131,750000". */
export function formatarCusto(valor: string | null | undefined): string {
  return comMoeda(formatarCustoNumero(valor));
}

/** Markup (fator) nas casas de markup da empresa: "1,894120". */
export function formatarMarkup(valor: string | null | undefined): string {
  return formatarValor(valor, 'markup');
}

/** Percentual com o valor ja em pontos percentuais (a API devolve "20.00" para 20%): "20,00%" (casas de percentual da empresa). */
export function formatarPercentual(valor: string | null | undefined): string {
  const texto = formatarValor(valor, 'percentual');
  return texto === '—' ? texto : `${texto}%`;
}

/** Pontos percentuais sem o sinal de % (queda de 1,25 pp). */
export function formatarPontos(valor: string | null | undefined): string {
  return formatarValor(valor, 'percentual');
}

/**
 * Fracao da API ("0.2184" = 21,84%) como percentual: "21,84%". O ponto decimal e deslocado duas casas na propria string
 * (sem multiplicar) e entao arredondado HALF_UP nas casas de percentual. Negativo e preservado.
 */
export function formatarFracaoPercentual(fracao: string | null | undefined): string {
  const pontos = deslocarDuasCasas(fracao);
  return pontos === null ? '—' : formatarPercentual(pontos);
}

/** Fracao da API como percentual com `casas` casas fixas ("0.2184", 4 = "21,8400%"). Mesmo deslocamento de `formatarFracaoPercentual`. */
export function formatarFracaoPercentualCasas(fracao: string | null | undefined, casas: number): string {
  const pontos = deslocarDuasCasas(fracao);
  if (pontos === null) return '—';
  const texto = formatarArredondado(pontos, casas);
  return texto === '—' ? texto : `${texto}%`;
}

/** Desloca o ponto duas casas na string da fracao (sem multiplicar): "0.2184" vira "21.84". Invalido: null. */
function deslocarDuasCasas(fracao: string | null | undefined): string | null {
  if (!ehDecimal(fracao)) return null;
  const m = RE_DECIMAL.exec(fracao.trim());
  if (!m) return null;
  const sinal = m[1] ?? '';
  const inteiro = m[2] ?? '0';
  const frac = (m[3] ?? '').padEnd(2, '0');
  const pontos = `${inteiro}${frac.slice(0, 2)}`.replace(/^0+(?=\d)/, '');
  const resto = frac.slice(2);
  return `${sinal}${pontos}${resto ? `.${resto}` : ''}`;
}

/** O valor decimal (string) e zero? Sem Number: so olha os digitos. Invalido: false. */
export function ehZero(valor: string | null | undefined): boolean {
  return ehDecimal(valor) && /^-?0*(\.0*)?$/.test(valor.trim());
}

/** Contagem inteira para exibicao (indice, total). */
export function formatarContagem(n: number): string {
  return Number.isSafeInteger(n) ? ptBR.format(n) : '—';
}

/** Horario "HH:MM" para "Salvo as HH:MM". */
export function formatarHora(data: Date): string {
  return HORA.format(data);
}

const DATA_HORA = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/** Data e hora de um instante ISO ("2026-10-17T15:41:47.574Z") no fuso local; invalido vira '—'. */
export function formatarDataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? '—' : DATA_HORA.format(t);
}

/**
 * Texto digitado pelo usuario ("25,00", "1.234,5", "25.5") para a string decimal da API ("25.00", "1234.5").
 * So reescreve caracteres (nenhuma conta). Vazio ou invalido: null.
 */
export function decimalDigitado(texto: string): string | null {
  const t = texto.trim().replace(/^R\$\s*/, '');
  if (!t) return null;
  const normal = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : /^\d{1,3}(\.\d{3})+$/.test(t) ? t.replace(/\./g, '') : t;
  return /^\d+(\.\d+)?$/.test(normal) ? normal : null;
}

/** Data ISO "2026-10-17" (ou instante ISO) como "17/10/2026". So reordena os campos; invalido vira '—'. */
export function formatarData(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
}

const MES_ANO = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** Mes e ano para o cabecalho do calendario de publicacoes: "Dezembro de 2026" (mes de 1 a 12). */
export function formatarMesAno(ano: number, mes: number): string {
  const texto = MES_ANO.format(new Date(Date.UTC(ano, mes - 1, 1)));
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** Idade de um instante ISO em relacao a `agora`: "hoje", "há 1 dia", "há 12 dias". Instante invalido vira '—'. */
export function formatarIdade(iso: string | null | undefined, agora: Date = new Date()): string {
  const t = iso ? new Date(iso).getTime() : Number.NaN;
  if (Number.isNaN(t)) return '—';
  const dias = Math.max(0, Math.floor((agora.getTime() - t) / 86_400_000));
  if (dias === 0) return 'hoje';
  return dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
}

/**
 * Fracao da API ("0.01650000") como texto de campo de percentual pt-BR ("1,65"): desloca o ponto duas casas na string
 * e tira zeros a direita ("0.18000000" vira "18"). Sem conta; invalido vira ''. Inverso de `percentualParaTaxa` (contratos).
 */
export function fracaoParaPercentualTexto(fracao: string | null | undefined): string {
  if (!ehDecimal(fracao)) return '';
  const m = RE_DECIMAL.exec(fracao.trim());
  if (!m || m[1]) return '';
  const frac = (m[3] ?? '').padEnd(2, '0');
  const inteiro = `${m[2] ?? '0'}${frac.slice(0, 2)}`.replace(/^0+(?=\d)/, '');
  const resto = frac.slice(2).replace(/0+$/, '');
  return resto ? `${inteiro},${resto}` : inteiro;
}

/** Taxa em fracao da API ("0.18000000") como percentual de cadastro: "18,00%"; sem zeros a direita alem de 2 casas ("1,65%", "0,6525%"). */
export function formatarTaxa(fracao: string | null | undefined): string {
  const t = fracaoParaPercentualTexto(fracao);
  return t === '' ? '—' : `${formatarDecimal(t.replace(',', '.'), 2)}%`;
}

/** Quantidade de cadastro ("2.000000" vira "2", "1.500000" vira "1,5"): tira os zeros de escala, nunca arredonda. */
export function formatarQuantidade(valor: string | null | undefined): string {
  if (!ehDecimal(valor)) return '—';
  const v = valor.trim();
  return formatarDecimal(v.includes('.') ? v.replace(/\.?0+$/, '') : v, 0);
}

/**
 * String decimal da API ("25.50000000") como texto de campo de valor pt-BR ("25,5"): troca o ponto por virgula e tira zeros
 * a direita. Sem conta; invalido vira ''. Inverso de `dinheiroDigitado` (contratos).
 */
export function decimalParaTextoCampo(valor: string | null | undefined): string {
  if (!ehDecimal(valor)) return '';
  const [inteiro = '0', fracao = ''] = valor.trim().split('.');
  const resto = fracao.replace(/0+$/, '');
  return resto ? `${inteiro},${resto}` : inteiro;
}

const UMA_CASA = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

/** Tamanho de arquivo para exibicao ("512 B", "38 KB", "1,5 MB"). Invalido vira '—'. */
export function formatarTamanhoArquivo(bytes: number): string {
  if (!Number.isSafeInteger(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${UMA_CASA.format(bytes / 1024)} KB`;
  return `${UMA_CASA.format(bytes / (1024 * 1024))} MB`;
}

/** Duracao em segundos para o cronometro da gravacao ("0:07", "1:05"). */
export function formatarDuracao(segundos: number): string {
  if (!Number.isSafeInteger(segundos) || segundos < 0) return '—';
  return `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, '0')}`;
}
